import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Loader2 } from 'lucide-react';
import { useExpenseCategories, useCreateExpenseCategory } from '@/hooks/finance/useExpenseCategories';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useAssignCostPositionToDepartment } from '@/hooks/finance/useDepartmentCostPositions';
import { toast } from 'sonner';
import { z } from 'zod';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import { generateNextCostPositionCode, isDuplicateCodeError } from '@/lib/cost-position-code';

const costPositionSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  code: z.string().trim().min(1, 'Code is required').max(50, 'Code must be less than 50 characters'),
  parent_id: z.string().min(1, 'Please select a cost group'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
});

export const QuickAddCostPosition = () => {
  const { data: categories } = useExpenseCategories();
  const { data: departments } = useDepartments();
  const createCategory = useCreateExpenseCategory();
  const assignToDepartment = useAssignCostPositionToDepartment();
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    parent_id: '',
    description: '',
    department_id: '',
    default_business_unit: '' as BusinessUnit | '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Get active departments for selection
  const activeDepartments = departments?.filter(d => d.is_active) || [];

  // Get cost groups for parent selection
  const costGroups = categories?.filter(c => c.category_type === 'cost_group') || [];

  // Auto-generate code based on parent — collision-safe across all categories
  const generateCode = (parentId: string, name: string) => {
    const parent = costGroups.find(g => g.id === parentId);
    if (!parent || !name) return '';
    const allCodes = (categories || []).map(c => c.code).filter(Boolean) as string[];
    return generateNextCostPositionCode(parent.code, allCodes);
  };

  const handleParentChange = (parentId: string) => {
    const newCode = generateCode(parentId, formData.name);
    setFormData(prev => ({
      ...prev,
      parent_id: parentId,
      code: newCode || prev.code,
    }));
    setErrors(prev => ({ ...prev, parent_id: '' }));
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => {
      const newCode = formData.parent_id ? generateCode(formData.parent_id, name) : prev.code;
      return {
        ...prev,
        name,
        code: newCode || prev.code,
      };
    });
    setErrors(prev => ({ ...prev, name: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form data
    const result = costPositionSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    try {
      // Inherit cash_flow_type from parent hierarchy
      const parentGroup = costGroups.find(g => g.id === formData.parent_id);
      const parentCategory = parentGroup ? categories?.find(c => c.id === parentGroup.parent_id) : null;
      const inheritedCashFlowType = parentGroup?.cash_flow_type || parentCategory?.cash_flow_type || 'operating';

      const newCategory = await createCategory.mutateAsync({
        name: formData.name.trim(),
        code: formData.code.toUpperCase().trim(),
        parent_id: formData.parent_id,
        description: formData.description?.trim() || undefined,
        category_type: 'cost_position',
        is_recordable: true,
        cash_flow_type: inheritedCashFlowType,
        default_business_unit: formData.default_business_unit || null,
      });
      
      // If a department is selected, link the cost position to it
      if (formData.department_id && newCategory?.id) {
        await assignToDepartment.mutateAsync({
          department_id: formData.department_id,
          category_id: newCategory.id,
        });
        toast.success(`Cost position "${formData.name}" created and linked to department`);
      } else {
        toast.success(`Cost position "${formData.name}" created successfully`);
      }
      
      // Reset form
      setFormData({
        name: '',
        code: '',
        parent_id: '',
        description: '',
        department_id: '',
        default_business_unit: '',
      });
    } catch (error: any) {
      if (isDuplicateCodeError(error)) {
        const allCodes = (categories || []).map(c => c.code).filter(Boolean) as string[];
        const parent = costGroups.find(g => g.id === formData.parent_id);
        const suggestion = parent ? generateNextCostPositionCode(parent.code, [...allCodes, formData.code.toUpperCase()]) : '';
        setErrors({ code: `Code already in use${suggestion ? ` — try ${suggestion}` : ''}` });
        toast.error('A cost position with this code already exists. Please choose a different code.');
      } else {
        toast.error(error?.message || 'Failed to create cost position');
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Quick Add Cost Position
        </CardTitle>
        <CardDescription>
          Add a new recordable cost position to an existing cost group
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="parent_id">Cost Group *</Label>
              <Select
                value={formData.parent_id}
                onValueChange={handleParentChange}
              >
                <SelectTrigger className={errors.parent_id ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a cost group" />
                </SelectTrigger>
                <SelectContent>
                  {costGroups.map((group) => {
                    // Find the parent expense category for context
                    const parentCategory = categories?.find(c => c.id === group.parent_id);
                    return (
                      <SelectItem key={group.id} value={group.id}>
                        {parentCategory ? `${parentCategory.name} > ` : ''}{group.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {errors.parent_id && (
                <p className="text-sm text-destructive">{errors.parent_id}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Cost Position Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., Transportation"
                className={errors.name ? 'border-destructive' : ''}
                maxLength={100}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code (auto-generated)</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }));
                  setErrors(prev => ({ ...prev, code: '' }));
                }}
                placeholder="e.g., COGS-DEL-001"
                className={errors.code ? 'border-destructive' : ''}
                maxLength={50}
              />
              {errors.code && (
                <p className="text-sm text-destructive">{errors.code}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
                maxLength={500}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="department_id">Link to Department (optional)</Label>
            <Select
              value={formData.department_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, department_id: value === 'none' ? '' : value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a department (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No department</SelectItem>
                {activeDepartments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name} ({dept.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Linking to a department restricts this cost position to that department's users
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_business_unit">Default Business Unit (optional)</Label>
            <Select
              value={formData.default_business_unit || 'none'}
              onValueChange={(value) => setFormData(prev => ({ ...prev, default_business_unit: value === 'none' ? '' : value as BusinessUnit }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="None — choose per expense" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None — choose per expense</SelectItem>
                {BUSINESS_UNITS.map((bu) => (
                  <SelectItem key={bu.value} value={bu.value}>{bu.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Auto-fills the BU on expenses that pick this cost position.</p>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={createCategory.isPending}>
              {createCategory.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Cost Position
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
